/**
 * Markdown do Discord → árvore. Quem desenha é a interface (React DOM no
 * navegador, React Native no celular); aqui só a regra, igual nos dois.
 *
 * Segue o comportamento do cliente do Discord (que usa o simple-markdown):
 * `**negrito**`, `*itálico*`/`_itálico_`, `__sublinhado__`, `~~riscado~~`,
 * `||spoiler||`, `` `código` ``, blocos ```` ``` ````, citação (`> ` e `>>> `),
 * títulos (`#`, `##`, `###`), subtexto (`-# `), listas, links (soltos,
 * `<…>` sem prévia e `[texto](url)`), menções `<@id>` `<@&id>` `<#id>`,
 * `@everyone`/`@here`, emojis `<:nome:id>` e datas `<t:unix:estilo>`.
 */

export type TimestampStyle = 't' | 'T' | 'd' | 'D' | 'f' | 'F' | 'R';

export type MarkdownNode =
  | { type: 'text'; content: string }
  | { type: 'br' }
  | {
      type: 'strong' | 'em' | 'underline' | 'strike' | 'spoiler';
      children: MarkdownNode[];
    }
  | { type: 'inlineCode'; content: string }
  | { type: 'codeBlock'; lang: string | null; content: string }
  | { type: 'blockQuote'; children: MarkdownNode[] }
  | { type: 'heading'; level: 1 | 2 | 3; children: MarkdownNode[] }
  | { type: 'subtext'; children: MarkdownNode[] }
  | {
      type: 'list';
      ordered: boolean;
      start: number;
      items: MarkdownNode[][];
    }
  | {
      type: 'link';
      url: string;
      children: MarkdownNode[];
      /** `[texto](url)`: o texto não é o endereço (a interface avisa antes de abrir). */
      masked: boolean;
      /** `<url>`: sem prévia (embed). */
      suppressEmbed: boolean;
    }
  | { type: 'userMention'; id: string }
  | { type: 'roleMention'; id: string }
  | { type: 'channelMention'; id: string }
  | { type: 'everyone' }
  | { type: 'here' }
  | { type: 'customEmoji'; id: string; name: string; animated: boolean }
  | { type: 'timestamp'; unix: number; style: TimestampStyle | null }
  /** Comando de barra citado (`</nome:id>`): fica só como texto. */
  | { type: 'command'; name: string; id: string };

export type ParseOptions = {
  /**
   * Só o que vale numa linha (títulos de canal, prévia de notificação): sem
   * blocos (título, citação, lista, bloco de código).
   */
  inline?: boolean;
};

type State = {
  inline: boolean;
  /** Dentro de citação: citação não aninha. */
  inQuote: boolean;
  /** Dentro de link: link não aninha. */
  inLink: boolean;
  depth: number;
};

type Rule = {
  /** Só no começo de linha (títulos, citação, lista, subtexto). */
  lineStart?: boolean;
  /** Bloco: fica de fora no modo `inline`. */
  block?: boolean;
  match(source: string, state: State, previous: string): RegExpExecArray | null;
  parse(
    capture: RegExpExecArray,
    state: State,
  ): MarkdownNode | MarkdownNode[] | null;
};

const MAX_DEPTH = 12;

function exec(regex: RegExp, source: string) {
  return regex.exec(source);
}

function nested(content: string, state: State): MarkdownNode[] {
  return parseNodes(content, { ...state, depth: state.depth + 1 });
}

const URL_START = /^https?:\/\/[^\s<]+/;

/** Tira do fim do link a pontuação de frase e o `)` sem par (como o Discord). */
function trimUrl(url: string): string {
  let end = url.length;
  for (;;) {
    const char = url[end - 1];
    if ('.,:;"\'!?'.includes(char)) {
      end--;
      continue;
    }
    if (char === ')') {
      const text = url.slice(0, end);
      const open = (text.match(/\(/g) ?? []).length;
      const close = (text.match(/\)/g) ?? []).length;
      if (close > open) {
        end--;
        continue;
      }
    }
    break;
  }
  return url.slice(0, end);
}

const RULES: Rule[] = [
  // --- Blocos -----------------------------------------------------------
  {
    block: true,
    match: (source) =>
      exec(/^```(?:([a-zA-Z0-9_+\-.#]+?)\n)?\n*([^\n][\s\S]*?)\n*```/, source),
    parse: (capture) => ({
      type: 'codeBlock',
      lang: capture[1] ?? null,
      content: capture[2],
    }),
  },
  {
    block: true,
    lineStart: true,
    match: (source, state) =>
      state.inQuote
        ? null
        : exec(/^ *>>> ([\s\S]*)|^( *> [^\n]*(?:\n *> [^\n]*)*)\n?/, source),
    parse: (capture, state) => {
      const content =
        capture[1] !== undefined
          ? capture[1]
          : capture[2]
              .split('\n')
              .map((line) => line.replace(/^ *> /, ''))
              .join('\n');
      return {
        type: 'blockQuote',
        children: nested(content, { ...state, inQuote: true }),
      };
    },
  },
  {
    block: true,
    lineStart: true,
    match: (source) => exec(/^(#{1,3}) +([^\n]+?) *(?:\n|$)/, source),
    parse: (capture, state) => ({
      type: 'heading',
      level: capture[1].length as 1 | 2 | 3,
      children: nested(capture[2], state),
    }),
  },
  {
    block: true,
    lineStart: true,
    match: (source) => exec(/^-# +([^\n]+?) *(?:\n|$)/, source),
    parse: (capture, state) => ({
      type: 'subtext',
      children: nested(capture[1], state),
    }),
  },
  {
    block: true,
    lineStart: true,
    match: (source) =>
      exec(
        /^(?:[*-]|\d{1,9}\.) +[^\n]*(?:\n(?:[*-]|\d{1,9}\.| {2,}) *[^\n]*)*\n?/,
        source,
      ),
    parse: (capture, state) => parseList(capture[0], state),
  },
  // --- Em linha ---------------------------------------------------------
  {
    match: (source) => exec(/^\\([^0-9A-Za-z\s])/, source),
    parse: (capture) => ({ type: 'text', content: capture[1] }),
  },
  {
    match: (source) => exec(/^(`+)([\s\S]*?[^`])\1(?!`)/, source),
    parse: (capture) => ({ type: 'inlineCode', content: capture[2] }),
  },
  {
    match: (source) => exec(/^\|\|([\s\S]+?)\|\|/, source),
    parse: (capture, state) => ({
      type: 'spoiler',
      children: nested(capture[1], state),
    }),
  },
  {
    match: (source, state) =>
      state.inLink ? null : exec(/^<(https?:\/\/[^\s>]+)>/, source),
    parse: (capture) => ({
      type: 'link',
      url: capture[1],
      children: [{ type: 'text', content: capture[1] }],
      masked: false,
      suppressEmbed: true,
    }),
  },
  {
    match: (source) => exec(/^<@!?(\d{1,20})>/, source),
    parse: (capture) => ({ type: 'userMention', id: capture[1] }),
  },
  {
    match: (source) => exec(/^<@&(\d{1,20})>/, source),
    parse: (capture) => ({ type: 'roleMention', id: capture[1] }),
  },
  {
    match: (source) => exec(/^<#(\d{1,20})>/, source),
    parse: (capture) => ({ type: 'channelMention', id: capture[1] }),
  },
  {
    match: (source) => exec(/^<(a?):([A-Za-z0-9_]{1,32}):(\d{1,20})>/, source),
    parse: (capture) => ({
      type: 'customEmoji',
      animated: capture[1] === 'a',
      name: capture[2],
      id: capture[3],
    }),
  },
  {
    match: (source) => exec(/^<t:(-?\d{1,17})(?::([tTdDfFR]))?>/, source),
    parse: (capture) => ({
      type: 'timestamp',
      unix: Number(capture[1]),
      style: (capture[2] as TimestampStyle | undefined) ?? null,
    }),
  },
  {
    match: (source) => exec(/^<\/([^:<>\n]{1,100}):(\d{1,20})>/, source),
    parse: (capture) => ({ type: 'command', name: capture[1], id: capture[2] }),
  },
  {
    match: (source, state) =>
      state.inLink
        ? null
        : exec(
            /^\[((?:\[[^\]\n]*\]|[^[\]\n])+)\]\(\s*<?(https?:\/\/[^\s>)]+)>?\s*\)/,
            source,
          ),
    parse: (capture, state) => ({
      type: 'link',
      url: capture[2],
      children: parseNodes(capture[1], {
        ...state,
        inLink: true,
        depth: state.depth + 1,
      }),
      masked: true,
      suppressEmbed: false,
    }),
  },
  {
    match: (source, state) => {
      if (state.inLink) return null;
      const found = URL_START.exec(source);
      if (!found) return null;
      const url = trimUrl(found[0]);
      const result = [url] as unknown as RegExpExecArray;
      result.index = 0;
      return url.length > 'https://'.length ? result : null;
    },
    parse: (capture) => ({
      type: 'link',
      url: capture[0],
      children: [{ type: 'text', content: capture[0] }],
      masked: false,
      suppressEmbed: false,
    }),
  },
  {
    match: (source) => exec(/^@everyone/, source),
    parse: () => ({ type: 'everyone' }),
  },
  {
    match: (source) => exec(/^@here/, source),
    parse: () => ({ type: 'here' }),
  },
  {
    match: (source) => exec(/^\*\*([\s\S]+?)\*\*(?!\*)/, source),
    parse: (capture, state) => ({
      type: 'strong',
      children: nested(capture[1], state),
    }),
  },
  {
    match: (source) => exec(/^__([\s\S]+?)__(?!_)/, source),
    parse: (capture, state) => ({
      type: 'underline',
      children: nested(capture[1], state),
    }),
  },
  {
    // `*itálico*`: abre colado no texto e não confunde com `**`.
    match: (source) =>
      exec(
        /^\*(?=\S)((?:\*\*|\\[\s\S]|\s+(?:\\[\s\S]|[^\s*\\]|\*\*)|[^\s*\\])+?)\*(?!\*)/,
        source,
      ),
    parse: (capture, state) => ({
      type: 'em',
      children: nested(capture[1], state),
    }),
  },
  {
    // `_itálico_` só fora de palavra (`nome_de_arquivo` fica como está).
    match: (source, _state, previous) =>
      /[\p{L}\p{N}]/u.test(previous)
        ? null
        : exec(/^_((?:__|\\[\s\S]|[^\\_])+?)_(?![\p{L}\p{N}_])/u, source),
    parse: (capture, state) => ({
      type: 'em',
      children: nested(capture[1], state),
    }),
  },
  {
    match: (source) => exec(/^~~([\s\S]+?)~~(?!~)/, source),
    parse: (capture, state) => ({
      type: 'strike',
      children: nested(capture[1], state),
    }),
  },
  {
    match: (source) => exec(/^\n/, source),
    parse: () => ({ type: 'br' }),
  },
  {
    // Texto até o próximo caractere que pode abrir alguma regra.
    match: (source) =>
      exec(/^[\s\S]+?(?=[\\*_~|`<[@\n#>-]|https?:\/\/|$)/, source) ??
      exec(/^[\s\S]/, source),
    parse: (capture) => ({ type: 'text', content: capture[0] }),
  },
];

/** Lista (`- `, `* ` ou `1. `); linhas com 2+ espaços entram na lista de dentro. */
function parseList(block: string, state: State): MarkdownNode {
  const lines = block.replace(/\n$/, '').split('\n');
  const first = /^(?:([*-])|(\d{1,9})\.) +/.exec(lines[0]);
  const ordered = !!first?.[2];
  const start = ordered ? Number(first?.[2]) : 1;
  const items: string[][] = [];
  for (const line of lines) {
    const marker = /^(?:[*-]|\d{1,9}\.) +(.*)$/.exec(line);
    if (marker && !/^ /.test(line)) {
      items.push([marker[1]]);
    } else if (items.length) {
      items[items.length - 1].push(line.replace(/^ {2}/, ''));
    }
  }
  return {
    type: 'list',
    ordered,
    start,
    items: items.map((item) => {
      const [text, ...rest] = item;
      const nodes = nested(text, { ...state, inQuote: state.inQuote });
      if (rest.length) {
        nodes.push(...nested(rest.join('\n'), state));
      }
      return nodes;
    }),
  };
}

function parseNodes(source: string, state: State): MarkdownNode[] {
  if (state.depth > MAX_DEPTH) return [{ type: 'text', content: source }];
  const nodes: MarkdownNode[] = [];
  let rest = source;
  let previous = '';
  let lineStart = true;
  while (rest.length) {
    let matched = false;
    for (const rule of RULES) {
      if (rule.block && state.inline) continue;
      if (rule.lineStart && !lineStart) continue;
      const capture = rule.match(rest, state, previous);
      if (!capture) continue;
      const result = rule.parse(capture, state);
      if (result === null) continue;
      const consumed = capture[0];
      for (const node of Array.isArray(result) ? result : [result]) {
        pushNode(nodes, node);
      }
      rest = rest.slice(consumed.length);
      previous = consumed[consumed.length - 1] ?? previous;
      // Bloco que terminou em quebra de linha deixa a próxima no começo de linha.
      lineStart =
        consumed.endsWith('\n') ||
        (rule.block === true && (rule.lineStart ?? false));
      matched = true;
      break;
    }
    if (!matched) {
      pushNode(nodes, { type: 'text', content: rest[0] });
      previous = rest[0];
      lineStart = rest[0] === '\n';
      rest = rest.slice(1);
    }
  }
  return nodes;
}

function pushNode(nodes: MarkdownNode[], node: MarkdownNode): void {
  const last = nodes[nodes.length - 1];
  if (node.type === 'text' && last?.type === 'text') {
    nodes[nodes.length - 1] = {
      type: 'text',
      content: last.content + node.content,
    };
    return;
  }
  nodes.push(node);
}

/** Markdown do Discord → árvore. */
export function parseMarkdown(
  source: string,
  options: ParseOptions = {},
): MarkdownNode[] {
  return parseNodes(source, {
    inline: !!options.inline,
    inQuote: false,
    inLink: false,
    depth: 0,
  });
}

// ---------------------------------------------------------------------------
// Emojis grandes e texto puro
// ---------------------------------------------------------------------------

const EMOJI_SEQUENCE =
  /\p{Regional_Indicator}\p{Regional_Indicator}|[#*0-9]️?⃣|\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?(?:‍\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?)*/gu;

/** Quantos emojis há num texto que só tem emojis e espaços (nulo se tem outra coisa). */
function emojiOnlyCount(text: string): number | null {
  const found = text.match(EMOJI_SEQUENCE) ?? [];
  const leftover = text.replace(EMOJI_SEQUENCE, '').replace(/[\s️]/g, '');
  return leftover ? null : found.length;
}

/**
 * Mensagem só de emojis (até 30, como o Discord): a interface desenha os
 * emojis grandes.
 */
export function isJumboEmoji(nodes: MarkdownNode[], limit = 30): boolean {
  let count = 0;
  for (const node of nodes) {
    if (node.type === 'customEmoji') {
      count++;
    } else if (node.type === 'text') {
      const emojis = emojiOnlyCount(node.content);
      if (emojis === null) return false;
      count += emojis;
    } else if (node.type !== 'br') {
      return false;
    }
  }
  return count > 0 && count <= limit;
}

export type PlainTextResolvers = {
  user?: (id: string) => string;
  role?: (id: string) => string;
  channel?: (id: string) => string;
  timestamp?: (unix: number, style: TimestampStyle | null) => string;
};

/**
 * Árvore → texto simples (prévia de notificação, resposta, busca): menções
 * com o nome, spoiler escondido, sem marcação.
 */
export function toPlainText(
  nodes: MarkdownNode[],
  resolvers: PlainTextResolvers = {},
): string {
  return nodes
    .map((node): string => {
      switch (node.type) {
        case 'text':
          return node.content;
        case 'br':
          return '\n';
        case 'inlineCode':
        case 'codeBlock':
          return node.content;
        case 'spoiler':
          return '▒▒▒▒';
        case 'list':
          return node.items
            .map((item) => `• ${toPlainText(item, resolvers)}`)
            .join('\n');
        case 'link':
          return node.masked ? toPlainText(node.children, resolvers) : node.url;
        case 'userMention':
          return `@${resolvers.user?.(node.id) ?? 'desconhecido'}`;
        case 'roleMention':
          return `@${resolvers.role?.(node.id) ?? 'cargo-desconhecido'}`;
        case 'channelMention':
          return `#${resolvers.channel?.(node.id) ?? 'canal-desconhecido'}`;
        case 'everyone':
          return '@everyone';
        case 'here':
          return '@here';
        case 'customEmoji':
          return `:${node.name}:`;
        case 'timestamp':
          return (
            resolvers.timestamp?.(node.unix, node.style) ??
            new Date(node.unix * 1000).toISOString()
          );
        case 'command':
          return `/${node.name}`;
        default:
          return toPlainText(node.children, resolvers);
      }
    })
    .join('');
}
